import React, { useState, useRef, useEffect, useMemo } from 'react';
import { View, Heading, Text, colors, BasicChip } from '@fairygodboss/ui';
import window from 'global/window';

import { labelToTextMap } from '../adviceHashMap';

import { isOverflown } from '../../../Events/EventLandingPage/components/helpers';
import { useAdvicePage } from '../AdvicePageContext';
import { EmitMetric } from '../../../Analytics/VisibilityPixel/VisibilityPixel';

export const CommunityDiscussionCard = ({
    content,
    fixedHeight,
    carouselView,
    index,
}) => {
    const {
        currentSlide,
        impressionMetricMap,
        onFireImpression,
        onDiscussionLinkClick,
    } = useAdvicePage();
    const [fireImpression, setFireImpression] = useState(false);

    const currentSlideRef = useRef(currentSlide);

    useEffect(() => {
        currentSlideRef.current = currentSlide;
    }, [currentSlide]);

    useEffect(() => {
        if (typeof currentSlide !== 'number') return;

        const cardSlide = Math.ceil((index + 1) / 6);
        const shouldFire = cardSlide === currentSlideRef.current;
        setFireImpression(shouldFire);
    }, [index, currentSlide, content]);

    let carouselViewBodyLimit;
    if (carouselView) {
        carouselViewBodyLimit = window?.innerWidth < 905 ? 2 : 4;
    }

    const [hasOverflow, setHasOverflow] = useState(true);
    const [maxBodyLines, setMaxBodyLines] = useState(
        carouselViewBodyLimit || 4
    );
    const [maxTitleLines] = useState(carouselView && 3);

    const [height, setHeight] = useState(
        carouselView?.height ? `${carouselView?.height}px` : null
    );
    const [width, setWidth] = useState(
        carouselView?.width ? `${carouselView?.width}px` : null
    );
    const [rerenderKey, setRerenderKey] = useState(0);

    useEffect(() => {
        requestAnimationFrame(() => {
            const randomNumber = Math.floor(Math.random() * 1001);
            setRerenderKey(randomNumber + 1);
        });
    }, []);

    useEffect(() => {
        const _width = carouselView?.width ? `${carouselView?.width}px` : null;

        if (_width !== width) {
            setWidth(carouselView?.width ? `${carouselView?.width}px` : null);
            setHeight(
                carouselView?.height ? `${carouselView?.height}px` : null
            );
        }
    }, [carouselView, width]);

    const containerRef = useRef();
    const bodyRef = useRef();
    const titleRef = useRef();

    const {
        commentCount,
        likes,
        contentUrl,
        title,
        description,
        topic,
        hash,
    } = content;

    const label = topic?.label;
    const commentText = commentCount === 1 ? 'Comment' : 'Comments';
    const likeText = likes === 1 ? 'Like' : 'Likes';

    useEffect(() => {
        if (carouselView) return;

        if (fixedHeight) {
            setMaxBodyLines(2);
        }
    }, [bodyRef, hasOverflow, fixedHeight, carouselView]);

    useEffect(() => {
        if (fixedHeight || carouselView) {
            return;
        }
        if (containerRef?.current) {
            setHasOverflow(isOverflown(containerRef?.current));
        }
    }, [containerRef, fixedHeight, carouselView]);

    const impressionMetrics = useMemo(
        () => ({
            misc_event_type: 'advice-hub-impression',
            community_content_key: hash,
            community_content_location: 'advice-content',
            impression: 1,
            // TODO: if we have sponsored company, add company ID
            company_id: [null],
            snowPlow: {
                component: 'tile',
                tileType: 'article',
                position: 'main',
                pageType: 'collection',
                pageSection: 'advice_landing',
            },
        }),
        [hash]
    );

    useEffect(() => {
        if (fireImpression && !impressionMetricMap[hash]) {
            onFireImpression({ hash });
            EmitMetric(impressionMetrics);
        }
    }, [
        fireImpression,
        impressionMetricMap,
        hash,
        impressionMetrics,
        onFireImpression,
        currentSlide,
    ]);

    return (
        <View
            width={width}
            height={height}
            display="flex"
            as="a"
            href={contentUrl}
            target="_self"
            key={rerenderKey}
            onClick={(event) =>
                onDiscussionLinkClick({ event, hash, contentUrl })
            }
        >
            <View height={height} width={{ _: '100%', md: 'inherit' }}>
                <View
                    id="testing-here"
                    px="16px"
                    py="16px"
                    overflow="hidden"
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    css={{
                        ':hover': {
                            boxShadow: '0px 24px 40px rgba(53, 53, 53, 0.16)',
                        },
                    }}
                    borderRadius="8px"
                    border={`1px solid ${colors.neutral[20]}`}
                    backgroundColor="#FFFFFF"
                    height={{
                        _: 'inherit',
                        sm: fixedHeight || height || '100%',
                        md: '100%',
                    }}
                    ref={containerRef}
                >
                    <View
                        borderRadius="0 0 8px 8px"
                        css={{
                            span: {
                                ':hover': {
                                    textDecoration: 'underline',
                                    color: colors.neutral[100],
                                },
                            },
                        }}
                    >
                        <View
                            css={{
                                span: {
                                    borderWidth: '0px',
                                    backgroundColor: colors.secondary[10],
                                    color: colors.secondary[70],
                                    fontWeight: '600',
                                },
                            }}
                        >
                            <BasicChip
                                size="medium"
                                text={labelToTextMap[label]}
                                variant="primary"
                                mb="8px"
                            />
                        </View>

                        <View ref={titleRef} overflow="hidden">
                            <Heading
                                variant={{
                                    _: 'headingXs',
                                    md: 'headingSm',
                                }}
                                color={colors.neutral[100]}
                                css={{
                                    ...(maxTitleLines
                                        ? {
                                              overflow: 'hidden',
                                              display: '-webkit-box',
                                              '-webkit-line-clamp': `${maxTitleLines}`,
                                              '-webkit-box-orient': 'vertical',
                                          }
                                        : null),
                                }}
                            >
                                {title}
                            </Heading>
                        </View>
                        <View overflow="hidden" color={colors.neutral[100]}>
                            <Text
                                pt="12px"
                                variant="bodyMd"
                                css={{
                                    ...(maxBodyLines
                                        ? {
                                              overflow: 'hidden',
                                              display: '-webkit-box',
                                              '-webkit-line-clamp': `${maxBodyLines}`,
                                              '-webkit-box-orient': 'vertical',
                                          }
                                        : null),
                                }}
                            >
                                {description.substring(0, 300)}
                            </Text>
                        </View>
                    </View>
                    <View
                        css={{
                            span: {
                                ':hover': {
                                    textDecoration: 'underline',
                                    color: colors.neutral[100],
                                },
                            },
                        }}
                        color={colors.neutral[80]}
                    >
                        <Text
                            pt="8px"
                            as="span"
                            margin="0"
                            textAlign="left"
                            variant="labelMd"
                        >
                            {`${likes} ${likeText}`}
                        </Text>
                        {'   •  '}
                        <Text
                            pt="8px"
                            as="span"
                            margin="0"
                            textAlign="left"
                            variant="labelMd"
                        >
                            {`${commentCount} ${commentText}`}
                        </Text>
                    </View>
                </View>
            </View>
        </View>
    );
};
